import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import {
  collection, doc, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc,
  query, orderBy, where, Timestamp
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import DialogoConfirmar from './Dialogo';

// ─── Paleta y estilos compartidos ────────────────────────────────────────────
const C = {
  indigo:   '#534AB7',
  violeta:  '#7B6FE8',
  neblina:  '#AFA9EC',
  alba:     '#EEEDFE',
  noche:    '#1a1440',
  fondo:    '#0e0b24',
  positivo: '#1D9E75',
  negativo: '#D85A30',
  atencion: '#BA7517',
  surface2: 'rgba(83,74,183,0.08)',
  borde:    'rgba(83,74,183,0.2)',
};

const S = {
  pagina:       { maxWidth: '1000px' },
  cabecera:     { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: `1px solid ${C.borde}`, padding: '24px 32px 0', backdropFilter: 'blur(8px)' },
  contenido:    { padding: '32px' },
  titulo:       { color: '#fff', fontWeight: 300, fontSize: '1.6rem', marginBottom: '16px' },
  pestanas:     { display: 'flex', gap: '4px', borderBottom: 'none', paddingBottom: '0' },
  pestana:      { background: 'none', border: 'none', padding: '10px 20px', fontSize: '0.88rem', cursor: 'pointer', borderRadius: '8px 8px 0 0', transition: 'all 0.15s', marginBottom: '-1px' },
  card:         { background: C.noche, border: `1px solid ${C.borde}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' },
  label:        { color: C.neblina, fontSize: '0.78rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' },
  input:        { background: C.surface2, border: `1px solid ${C.borde}`, borderRadius: '8px', padding: '10px 12px', color: '#fff', fontSize: '0.9rem', width: '100%', outline: 'none', boxSizing: 'border-box' },
  select:       { background: '#1a1440', border: `1px solid ${C.borde}`, borderRadius: '8px', padding: '10px 12px', color: '#fff', fontSize: '0.9rem', outline: 'none', colorScheme: 'dark' },
  inputError:   { border: `1px solid ${C.negativo}` },
  btn:          { background: C.indigo, border: 'none', borderRadius: '8px', padding: '10px 20px', color: '#fff', fontSize: '0.88rem', cursor: 'pointer', fontWeight: 500 },
  btnSec:       { background: 'none', border: `1px solid ${C.borde}`, borderRadius: '8px', padding: '10px 20px', color: C.neblina, fontSize: '0.88rem', cursor: 'pointer' },
  btnPeligro:   { background: 'none', border: `1px solid ${C.negativo}`, borderRadius: '8px', padding: '8px 16px', color: C.negativo, fontSize: '0.82rem', cursor: 'pointer' },
  btnSmall:     { background: C.indigo, border: 'none', borderRadius: '6px', padding: '6px 14px', color: '#fff', fontSize: '0.82rem', cursor: 'pointer' },
  btnSmallSec:  { background: 'none', border: `1px solid ${C.borde}`, borderRadius: '6px', padding: '6px 14px', color: C.neblina, fontSize: '0.82rem', cursor: 'pointer' },
  fila:         { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  col2:         { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  col3:         { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' },
  separador:    { borderTop: `1px solid ${C.borde}`, margin: '20px 0' },
  badge:        { display: 'inline-block', padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 500 },
  aviso:        { background: 'rgba(186,117,23,0.12)', border: `1px solid ${C.atencion}`, borderRadius: '8px', padding: '12px 16px', color: C.atencion, fontSize: '0.85rem', marginBottom: '16px' },
  exito:        { background: 'rgba(29,158,117,0.12)', border: `1px solid ${C.positivo}`, borderRadius: '8px', padding: '12px 16px', color: C.positivo, fontSize: '0.85rem', marginBottom: '16px' },
  error:        { background: 'rgba(216,90,48,0.12)', border: `1px solid ${C.negativo}`, borderRadius: '8px', padding: '12px 16px', color: C.negativo, fontSize: '0.85rem', marginBottom: '16px' },
  tabla:        { width: '100%', borderCollapse: 'collapse' },
  th:           { color: C.neblina, fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${C.borde}` },
  td:           { padding: '12px', borderBottom: `1px solid ${C.borde}`, color: '#fff', fontSize: '0.88rem', verticalAlign: 'middle' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function calcularCosteHora(datos) {
  const { coste_empresa, horas_diarias, dias_vacaciones, dias_festivos } = datos;
  const ce  = parseFloat(coste_empresa)   || 0;
  const hd  = parseFloat(horas_diarias)   || 8;
  const dv  = parseFloat(dias_vacaciones) || 22;
  const df  = parseFloat(dias_festivos)   || 14;
  // Días laborables al año: 365 - 104 fines de semana - vacaciones - festivos
  const laborables = 365 - 104 - dv - df;
  const horasAnuales = laborables * hd;
  const costeHora = horasAnuales > 0 ? ce / horasAnuales : 0;
  return { horasAnuales: Math.round(horasAnuales), costeHora: parseFloat(costeHora.toFixed(2)) };
}

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
}

function Badge({ texto, color }) {
  const colores = {
    activo:    { bg: 'rgba(29,158,117,0.15)',  fg: C.positivo },
    historico: { bg: 'rgba(175,169,236,0.15)', fg: C.neblina  },
    admin:     { bg: 'rgba(83,74,183,0.2)',    fg: C.violeta  },
    comercial: { bg: 'rgba(186,117,23,0.15)',  fg: C.atencion },
    empleado:  { bg: 'rgba(175,169,236,0.15)', fg: C.neblina  },
  };
  const c = colores[color] || colores.historico;
  return <span style={{ ...S.badge, background: c.bg, color: c.fg }}>{texto}</span>;
}

function etiquetaRoles(roles) {
  if (!roles || roles.length === 0) return 'Sin rol';
  if (roles.includes('admin')) return 'Administrador';
  return roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(' · ');
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────
function Configuracion({ perfil, usuario }) {
  const [pestana, setPestana] = useState('usuarios');

  const PESTANAS = [
    { id: 'usuarios',   label: 'Usuarios'           },
    { id: 'listas',     label: 'Listas'             },
    { id: 'objetivos',  label: 'Objetivos'          },
    { id: 'margen',     label: 'Margen empresa'     },
    { id: 'estructura', label: 'Estructura costes'  },
    { id: 'exportacion',label: 'Exportación'        },
  ];

  return (
    <div style={S.pagina}>
      {/* ── Cabecera sticky ── */}
      <div style={S.cabecera}>
        <h1 style={S.titulo}>Configuración</h1>
        <div style={S.pestanas}>
          {PESTANAS.map(p => (
            <button
              key={p.id}
              onClick={() => setPestana(p.id)}
              style={{
                ...S.pestana,
                color:        pestana === p.id ? '#fff'    : C.neblina,
                background:   pestana === p.id ? C.surface2: 'none',
                borderBottom: pestana === p.id ? `2px solid ${C.indigo}` : '2px solid transparent',
                fontWeight:   pestana === p.id ? 500 : 400,
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Contenido ── */}
      <div style={S.contenido}>
        {pestana === 'usuarios'    && <PestanaUsuarios usuarioActual={usuario} perfilActual={perfil} />}
        {pestana === 'listas'      && <PestanaListas />}
        {pestana === 'objetivos'   && <PestanaObjetivos />}
        {pestana === 'margen'      && <PestanaMargen />}
        {pestana === 'estructura'  && <PestanaEstructura />}
        {pestana === 'exportacion' && <PestanaExportacion />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PESTAÑA USUARIOS
// ════════════════════════════════════════════════════════════════════════════
function PestanaUsuarios({ usuarioActual, perfilActual }) {
  const [usuarios, setUsuarios]       = useState([]);
  const [cargando, setCargando]       = useState(true);
  const [seccion, setSeccion]         = useState('activos'); // 'activos' | 'historicos'
  const [modalUsuario, setModalUsuario] = useState(null);   // null | 'nuevo' | { ...usuario }
  const [modalTarifa, setModalTarifa]   = useState(null);   // null | { usuarioId, nombre }
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null); // { mensaje, onAceptar }

  const cargar = useCallback(async () => {
    setCargando(true);
    const snap = await getDocs(collection(db, 'usuarios'));
    const docs = snap.docs.map(d => {
      const data = d.data();
      // Migración: docs antiguos con campo rol (string) en vez de roles (array) y sin estado
      if (!data.roles && data.rol) data.roles = [data.rol];
      if (!data.roles) data.roles = ['empleado'];
      if (!data.estado) data.estado = 'activo';
      return { id: d.id, ...data };
    });
    setUsuarios(docs);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const activos    = usuarios.filter(u => u.estado === 'activo');
  const historicos = usuarios.filter(u => u.estado === 'historico');

  const darDeBaja = async (u) => {
    setConfirm({ mensaje: `¿Dar de baja a ${u.nombre}? Sus datos históricos se conservarán.`, onAceptar: async () => {
      await updateDoc(doc(db, 'usuarios', u.id), { estado: 'historico' });
      setMsg({ tipo: 'exito', texto: `${u.nombre} pasó a histórico.` });
      setConfirm(null);
      cargar();
    }});
    return;
  };

  const reactivar = async (u) => {
    await updateDoc(doc(db, 'usuarios', u.id), { estado: 'activo' });
    setMsg({ tipo: 'exito', texto: `${u.nombre} reactivado.` });
    cargar();
  };

  const enviarReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setMsg({ tipo: 'exito', texto: `Enlace de contraseña enviado a ${email}` });
    } catch {
      setMsg({ tipo: 'error', texto: 'No se pudo enviar el enlace. Comprueba el email.' });
    }
  };

  return (
    <div>
      {msg && (
        <div style={msg.tipo === 'exito' ? S.exito : S.error} onClick={() => setMsg(null)}>
          {msg.texto}
        </div>
      )}

      {/* Subsecciones */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[
          { id: 'activos',    label: `Activos (${activos.length})`       },
          { id: 'historicos', label: `Históricos (${historicos.length})` },
        ].map(s => (
          <button
            key={s.id}
            onClick={() => setSeccion(s.id)}
            style={{
              ...S.btnSec,
              background:  seccion === s.id ? C.surface2 : 'none',
              color:       seccion === s.id ? '#fff'     : C.neblina,
              borderColor: seccion === s.id ? C.indigo   : C.borde,
            }}
          >
            {s.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button style={S.btn} onClick={() => setModalUsuario('nuevo')}>
          + Nuevo usuario
        </button>
      </div>

      {cargando ? (
        <p style={{ color: C.neblina }}>Cargando...</p>
      ) : (
        <div style={S.card}>
          <table style={S.tabla}>
            <thead>
              <tr>
                <th style={S.th}>Nombre</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Rol</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Tarifas</th>
                <th style={S.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(seccion === 'activos' ? activos : historicos).map(u => (
                <FilaUsuario
                  key={u.id}
                  u={u}
                  esActual={u.id === usuarioActual?.uid}
                  seccion={seccion}
                  onEditar={() => setModalUsuario(u)}
                  onBaja={() => darDeBaja(u)}
                  onReactivar={() => reactivar(u)}
                  onReset={() => enviarReset(u.email)}
                  onTarifas={() => setModalTarifa({ usuarioId: u.id, nombre: u.nombre })}
                />
              ))}
              {(seccion === 'activos' ? activos : historicos).length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...S.td, color: C.neblina, textAlign: 'center', padding: '32px' }}>
                    {seccion === 'activos' ? 'No hay usuarios activos.' : 'No hay usuarios históricos.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalUsuario && (
        <ModalUsuario
          usuario={modalUsuario === 'nuevo' ? null : modalUsuario}
          onCerrar={() => setModalUsuario(null)}
          onGuardado={() => { setModalUsuario(null); cargar(); }}
          onMsg={setMsg}
        />
      )}

      {confirm && (
        <ModalConfirm
          mensaje={confirm.mensaje}
          onAceptar={confirm.onAceptar}
          onCancelar={() => setConfirm(null)}
          textoAceptar="Dar de baja"
        />
      )}

      {modalTarifa && (
        <ModalTarifas
          usuarioId={modalTarifa.usuarioId}
          nombre={modalTarifa.nombre}
          onCerrar={() => setModalTarifa(null)}
        />
      )}
    </div>
  );
}

function FilaUsuario({ u, esActual, seccion, onEditar, onBaja, onReactivar, onReset, onTarifas }) {
  const rolColor = u.roles?.includes('admin') ? 'admin'
    : u.roles?.includes('comercial') ? 'comercial' : 'empleado';

  return (
    <tr>
      <td style={S.td}>
        <span style={{ color: '#fff', fontWeight: 500 }}>{u.nombre}</span>
        {esActual && <span style={{ color: C.neblina, fontSize: '0.75rem', marginLeft: '8px' }}>(tú)</span>}
      </td>
      <td style={{ ...S.td, color: C.neblina, fontSize: '0.82rem' }}>{u.email || '—'}</td>
      <td style={S.td}><Badge texto={etiquetaRoles(u.roles)} color={rolColor} /></td>
      <td style={S.td}>
        <Badge
          texto={u.estado === 'activo' ? 'Activo' : 'Histórico'}
          color={u.estado === 'activo' ? 'activo' : 'historico'}
        />
      </td>
      <td style={S.td}>
        <button style={S.btnSmallSec} onClick={onTarifas}>Ver tarifas</button>
      </td>
      <td style={S.td}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={S.btnSmall} onClick={onEditar}>Editar</button>
          {seccion === 'activos' && u.email && (
            <button style={S.btnSmallSec} onClick={onReset}>Enviar contraseña</button>
          )}
          {seccion === 'activos' && !esActual && (
            <button style={{ ...S.btnSmallSec, borderColor: C.negativo, color: C.negativo }} onClick={onBaja}>
              Dar de baja
            </button>
          )}
          {seccion === 'historicos' && (
            <button style={S.btnSmall} onClick={onReactivar}>Reactivar</button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Modal crear/editar usuario ───────────────────────────────────────────────
function ModalUsuario({ usuario, onCerrar, onGuardado, onMsg }) {
  const esNuevo   = !usuario;
  const esHistorico = usuario?.estado === 'historico';

  const [form, setForm] = useState({
    nombre:  usuario?.nombre  || '',
    email:   usuario?.email   || '',
    roles:   usuario?.roles   || ['empleado'],
    estado:  usuario?.estado  || 'activo',
    uid:     '',
  });
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores]     = useState({});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleRol = (rol) => {
    if (rol === 'admin') { set('roles', ['admin']); return; }
    let nuevos = form.roles.filter(r => r !== 'admin');
    if (nuevos.includes(rol)) nuevos = nuevos.filter(r => r !== rol);
    else nuevos = [...nuevos, rol];
    if (nuevos.length === 0) nuevos = ['empleado'];
    set('roles', nuevos);
  };

  const validar = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = true;
    if (esNuevo && !form.email.trim()) e.email = true;
    if (esNuevo && form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (esNuevo && form.estado !== 'historico' && form.email.trim() && !/^[A-Za-z0-9]{20,40}$/.test(form.uid.trim())) e.uid = true;
    return e;
  };

  const guardar = async () => {
    const e = validar();
    if (Object.keys(e).length > 0) { setErrores(e); return; }
    setGuardando(true);
    try {
      if (esNuevo) {
        // Crear usuario en Firebase Auth + doc en Firestore
        // Si es histórico (sin email), solo creamos el doc
        if (form.estado === 'historico' || !form.email.trim()) {
          const ref = await addDoc(collection(db, 'usuarios'), {
            nombre: form.nombre.trim(),
            email:  form.email.trim() || null,
            roles:  form.roles,
            estado: 'historico',
            creado_en: Timestamp.now(),
          });
          onMsg({ tipo: 'exito', texto: `${form.nombre} añadido como histórico.` });
        } else {
          // Sesión 33: el registro desde el navegador está cerrado por seguridad. La cuenta se crea en la consola
          // de Firebase (Authentication → Agregar usuario) y aquí se crea su ficha con el UID que da la consola.
          const uid = form.uid.trim();
          const existente = await getDoc(doc(db, 'usuarios', uid));
          if (existente.exists()) throw new Error('Ya existe una ficha de usuario con ese UID.');
          await setDoc(doc(db, 'usuarios', uid), {
            nombre: form.nombre.trim(),
            email:  form.email.trim(),
            roles:  form.roles,
            estado: 'activo',
            creado_en: Timestamp.now(),
          });
          await sendPasswordResetEmail(auth, form.email.trim());
          onMsg({ tipo: 'exito', texto: `${form.nombre} creado. Se envió el enlace de contraseña a ${form.email}.` });
        }
      } else {
        // Editar — solo actualizamos nombre y roles
        await updateDoc(doc(db, 'usuarios', usuario.id), {
          nombre: form.nombre.trim(),
          roles:  form.roles,
        });
        onMsg({ tipo: 'exito', texto: `${form.nombre} actualizado.` });
      }
      onGuardado();
    } catch (err) {
      const msg = err.code === 'auth/email-already-in-use'
        ? 'Ese email ya está registrado en Firebase.'
        : err.message;
      onMsg({ tipo: 'error', texto: msg });
      setGuardando(false);
    }
  };

  return (
    <div style={estilosModal.overlay} onClick={onCerrar}>
      <div style={estilosModal.caja} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#fff', fontWeight: 400, marginBottom: '24px' }}>
          {esNuevo ? 'Nuevo usuario' : `Editar — ${usuario.nombre}`}
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={S.label}>Nombre *</label>
          <input
            style={{ ...S.input, ...(errores.nombre ? S.inputError : {}) }}
            value={form.nombre}
            onChange={e => set('nombre', e.target.value)}
            placeholder="Nombre completo"
          />
        </div>

        {esNuevo && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Email</label>
              <input
                style={{ ...S.input, ...(errores.email ? S.inputError : {}) }}
                value={form.email}
                onChange={e => set('email', e.target.value)}
                placeholder="email@estudio.com (dejar vacío si es histórico sin acceso)"
                type="email"
              />
              <p style={{ color: C.neblina, fontSize: '0.75rem', marginTop: '4px' }}>
                Sin email → se crea como empleado histórico (solo para datos de importación).
              </p>
            </div>

            {/* Sesión 34: el campo UID se ve desde el principio en las cuentas con acceso (antes aparecía solo al escribir el email) */}
            {esNuevo && form.estado !== 'historico' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={S.label}>UID de Firebase * <span style={{ textTransform: 'none', fontWeight: 400 }}>(identificador de la cuenta creada en la consola)</span></label>
                <input
                  style={{ ...S.input, ...(errores.uid ? S.inputError : {}) }}
                  value={form.uid}
                  onChange={e => set('uid', e.target.value)}
                  placeholder="Pega aquí el UID del usuario"
                />
                <p style={{ color: C.neblina, fontSize: '0.75rem', marginTop: '4px' }}>
                  Primero crea la cuenta en la consola de Firebase: Authentication → Usuarios → Agregar usuario (con este mismo email y una contraseña provisional). Copia el UID que aparece en la lista y pégalo aquí. Al guardar se envía a la persona el enlace para elegir su contraseña.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={S.label}>Tipo de cuenta</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['activo', 'historico'].map(e => (
                  <button
                    key={e}
                    onClick={() => set('estado', e)}
                    style={{
                      ...S.btnSmallSec,
                      background:  form.estado === e ? C.surface2 : 'none',
                      color:       form.estado === e ? '#fff'     : C.neblina,
                      borderColor: form.estado === e ? C.indigo   : C.borde,
                    }}
                  >
                    {e === 'activo' ? 'Activo (con acceso)' : 'Histórico (sin acceso)'}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div style={{ marginBottom: '24px' }}>
          <label style={S.label}>Roles</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['admin', 'comercial', 'empleado'].map(r => {
              const activo = form.roles.includes(r);
              return (
                <button
                  key={r}
                  onClick={() => toggleRol(r)}
                  style={{
                    ...S.btnSmallSec,
                    background:  activo ? C.indigo   : 'none',
                    color:       activo ? '#fff'     : C.neblina,
                    borderColor: activo ? C.indigo   : C.borde,
                  }}
                >
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              );
            })}
          </div>
          <p style={{ color: C.neblina, fontSize: '0.75rem', marginTop: '6px' }}>
            Admin siempre va solo. Comercial y Empleado son combinables.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button style={S.btnSec} onClick={onCerrar}>Cancelar</button>
          <button style={S.btn} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal tarifas ────────────────────────────────────────────────────────────
function ModalTarifas({ usuarioId, nombre, onCerrar }) {
  const [tarifas, setTarifas]     = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [formAbierto, setFormAbierto] = useState(false);
  const [form, setForm]           = useState({ coste_empresa: '', horas_diarias: '8', dias_vacaciones: '22', dias_festivos: '14', fecha_inicio: '', notas: '' });
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg]             = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null); // id de tarifa a eliminar

  const cargar = useCallback(async () => {
    setCargando(true);
    const snap = await getDocs(query(
      collection(db, 'usuarios', usuarioId, 'tarifas'),
      orderBy('fecha_inicio', 'desc')
    ));
    setTarifas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setCargando(false);
  }, [usuarioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const { horasAnuales, costeHora } = calcularCosteHora(form);

  const guardar = async () => {
    if (!form.coste_empresa || !form.fecha_inicio) {
      setMsg({ tipo: 'error', texto: 'Coste empresa y fecha de inicio son obligatorios.' });
      return;
    }
    setGuardando(true);
    try {
      // Cerrar tarifa anterior si existe
      const activa = tarifas.find(t => !t.fecha_fin);
      if (activa) {
        await updateDoc(doc(db, 'usuarios', usuarioId, 'tarifas', activa.id), {
          fecha_fin: form.fecha_inicio
        });
      }
      await addDoc(collection(db, 'usuarios', usuarioId, 'tarifas'), {
        coste_empresa:   parseFloat(form.coste_empresa),
        horas_diarias:   parseFloat(form.horas_diarias),
        dias_vacaciones: parseFloat(form.dias_vacaciones),
        dias_festivos:   parseFloat(form.dias_festivos),
        horas_anuales:   horasAnuales,
        coste_hora:      costeHora,
        fecha_inicio:    form.fecha_inicio,
        fecha_fin:       null,
        notas:           form.notas.trim(),
      });
      setMsg({ tipo: 'exito', texto: 'Tarifa guardada.' });
      setFormAbierto(false);
      setForm({ coste_empresa: '', horas_diarias: '8', dias_vacaciones: '22', dias_festivos: '14', fecha_inicio: '', notas: '' });
      cargar();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message });
    }
    setGuardando(false);
  };

  const eliminarTarifa = async (id) => {
    try {
      const tarifa = tarifas.find(t => t.id === id);
      const eraActiva = !tarifa?.fecha_fin;
      await deleteDoc(doc(db, 'usuarios', usuarioId, 'tarifas', id));
      // Si era la activa, reabrir la anterior (la más reciente con fecha_fin)
      if (eraActiva) {
        const anteriores = tarifas.filter(t => t.id !== id && t.fecha_fin).sort((a,b) => b.fecha_inicio.localeCompare(a.fecha_inicio));
        if (anteriores.length > 0) {
          await updateDoc(doc(db, 'usuarios', usuarioId, 'tarifas', anteriores[0].id), { fecha_fin: null });
        }
      }
      setMsg({ tipo: 'exito', texto: 'Tarifa eliminada.' });
      setConfirmEliminar(null);
      cargar();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message });
    }
  };

  return (
    <div style={estilosModal.overlay} onClick={onCerrar}>
      <div style={{ ...estilosModal.caja, maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <style>{`.metron-date::-webkit-calendar-picker-indicator{background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.6)' stroke-width='2'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'/%3E%3Cline x1='16' y1='2' x2='16' y2='6'/%3E%3Cline x1='8' y1='2' x2='8' y2='6'/%3E%3Cline x1='3' y1='10' x2='21' y2='10'/%3E%3C/svg%3E");background-repeat:no-repeat;background-size:16px;cursor:pointer;opacity:1;}.metron-date{color-scheme:dark;}`}</style>
        <h3 style={{ color: '#fff', fontWeight: 400, marginBottom: '4px' }}>Tarifas — {nombre}</h3>
        <p style={{ color: C.neblina, fontSize: '0.82rem', marginBottom: '20px' }}>
          Historial de coste/hora. Cada tarifa se aplica a los registros de horas de su período.
        </p>

        {msg && <div style={msg.tipo === 'exito' ? S.exito : S.error}>{msg.texto}</div>}

        {/* Botón nueva tarifa */}
        {!formAbierto && (
          <button style={{ ...S.btn, marginBottom: '20px' }} onClick={() => setFormAbierto(true)}>
            + Nueva tarifa
          </button>
        )}

        {/* Formulario nueva tarifa */}
        {formAbierto && (
          <div style={{ ...S.card, marginBottom: '20px' }}>
            <p style={{ color: '#fff', fontWeight: 500, marginBottom: '16px' }}>Nueva tarifa</p>
            <div style={S.col2}>
              <div>
                <label style={S.label}>Coste empresa anual (€) *</label>
                <input style={S.input} type="number" value={form.coste_empresa} onChange={e => set('coste_empresa', e.target.value)} placeholder="Ej: 32000" />
              </div>
              <div>
                <label style={S.label}>Fecha de inicio *</label>
                <input style={{ ...S.input, colorScheme: 'dark' }} className="metron-date" type="date" value={form.fecha_inicio} onChange={e => set('fecha_inicio', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Horas diarias</label>
                <input style={S.input} type="number" value={form.horas_diarias} onChange={e => set('horas_diarias', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Días de vacaciones</label>
                <input style={S.input} type="number" value={form.dias_vacaciones} onChange={e => set('dias_vacaciones', e.target.value)} />
              </div>
              <div>
                <label style={S.label}>Días festivos</label>
                <input style={S.input} type="number" value={form.dias_festivos} onChange={e => set('dias_festivos', e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ background: C.surface2, borderRadius: '8px', padding: '10px 12px' }}>
                  <p style={{ color: C.neblina, fontSize: '0.75rem', marginBottom: '4px' }}>Coste/hora calculado</p>
                  <p style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 500 }}>{formatEur(costeHora)}/h</p>
                  <p style={{ color: C.neblina, fontSize: '0.72rem' }}>{horasAnuales} h/año</p>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={S.label}>Notas (opcional)</label>
              <input style={S.input} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Ej: Actualización salarial 2025" />
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button style={S.btnSec} onClick={() => setFormAbierto(false)}>Cancelar</button>
              <button style={S.btn} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar tarifa'}
              </button>
            </div>
          </div>
        )}

        {/* Historial de tarifas */}
        {cargando ? (
          <p style={{ color: C.neblina }}>Cargando...</p>
        ) : tarifas.length === 0 ? (
          <p style={{ color: C.neblina, textAlign: 'center', padding: '24px' }}>Sin tarifas registradas.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {tarifas.map(t => (
              <div key={t.id} style={{
                background: C.surface2,
                border: `1px solid ${!t.fecha_fin ? C.indigo : C.borde}`,
                borderRadius: '10px',
                padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  {/* Período + badge */}
                  <div style={{ minWidth: '180px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ color: '#fff', fontSize: '0.88rem', fontWeight: 500 }}>{t.fecha_inicio}</span>
                      <span style={{ color: C.neblina }}>→</span>
                      <span style={{ color: t.fecha_fin ? C.neblina : C.positivo, fontSize: '0.88rem' }}>
                        {t.fecha_fin || 'Actual'}
                      </span>
                      {!t.fecha_fin && <Badge texto="Activa" color="activo" />}
                    </div>
                    {t.notas && <p style={{ color: C.neblina, fontSize: '0.78rem', margin: 0 }}>{t.notas}</p>}
                  </div>
                  {/* Datos económicos */}
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: C.neblina, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' }}>Coste empresa</p>
                      <p style={{ color: '#fff', fontWeight: 500, fontSize: '0.88rem', margin: 0 }}>{formatEur(t.coste_empresa)}</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: C.neblina, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' }}>Horas/año</p>
                      <p style={{ color: '#fff', fontWeight: 500, fontSize: '0.88rem', margin: 0 }}>{t.horas_anuales} h</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ color: C.neblina, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' }}>€/hora</p>
                      <p style={{ color: C.violeta, fontWeight: 600, fontSize: '1rem', margin: 0 }}>{formatEur(t.coste_hora)}/h</p>
                    </div>
                    <button
                      style={{ ...S.btnSmallSec, borderColor: C.negativo, color: C.negativo, alignSelf: 'center' }}
                      onClick={() => setConfirmEliminar(t.id)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: '24px', textAlign: 'right' }}>
          <button style={S.btnSec} onClick={onCerrar}>Cerrar</button>
        </div>

        {confirmEliminar && (
          <ModalConfirm
            mensaje="¿Eliminar esta tarifa? Si era la activa, la anterior pasará a ser la vigente."
            onAceptar={() => eliminarTarifa(confirmEliminar)}
            onCancelar={() => setConfirmEliminar(null)}
            textoAceptar="Eliminar"
          />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PESTAÑA LISTAS
// ════════════════════════════════════════════════════════════════════════════
const DATOS_INICIALES = {
  sectores: [
    'Comercial','Corporativo','Hostelería','Otro','Residencial','Sanitario',
  ],
  fuentes: [
    'Boca-oreja','Blog','Charla / Ponencia / Evento','Colaborador / Prescriptor',
    'Conocido / Cercanía','Email Marketing','Facebook','Facebook Ads','Google',
    'Google Ads','Instagram','LinkedIn','Otro','Otro proyecto / Cliente',
    'Prensa escrita','Proyecto propio','Recurrente','Teléfono','Twitter / X',
    'Web','YouTube',
  ],
  tipos_proyecto: [
    'Adaptación Proyecto y Dirección','Concurso','Ejecución','Equipamiento','Otro',
    'Proyecto y Dirección','Proyecto y Dirección y Ejecución','Restyling',
    'Restyling y Dirección','Solo Adaptación Proyecto','Solo Dirección / Consultoría',
    'Solo Proyecto',
  ],
  categorias_horas: [
    'Administración/Gestión','Comercial','Formación','Otro eIC','Otro eINES',
    'Reuniones internas','RRSS Instagram','RRSS Youtube',
  ],
};

function PestanaListas() {
  const LISTAS = [
    { id: 'sectores',        label: 'Sectores de proyecto'       },
    { id: 'fuentes',         label: 'Fuentes de lead'            },
    { id: 'tipos_proyecto',  label: 'Tipos de proyecto'          },
    { id: 'categorias_horas',label: 'Categorías de horas internas' },
  ];
  const [listaActiva, setListaActiva] = useState('sectores');
  const [inicializando, setInicializando] = useState(false);
  const [msgInit, setMsgInit] = useState(null);
  const [inicializado, setInicializado] = useState(false);
  const [confirmInit, setConfirmInit] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'config', 'listas', 'sectores'))
      .then(snap => { if (snap.size > 0) setInicializado(true); })
      .catch(() => {});
  }, []);

  const inicializarListas = async () => {
    setInicializando(true);
    try {
      for (const [lista, valores] of Object.entries(DATOS_INICIALES)) {
        for (let i = 0; i < valores.length; i++) {
          await addDoc(collection(db, 'config', 'listas', lista), { valor: valores[i], orden: i });
        }
      }
      await setDoc(doc(db, 'config', 'objetivos'), {
        2026: { proyectos:{clave:9,estelar:11}, margen:{clave:20,estelar:28}, velocidad:{clave:1.60,estelar:1.40}, resenias:{clave:75,estelar:90}, horas:{clave:1672,estelar:1712} }
      });
      setMsgInit({ tipo: 'exito', texto: '\u2713 Listas y objetivos 2026 cargados correctamente.' });
      setInicializado(true);
    } catch (err) {
      setMsgInit({ tipo: 'error', texto: err.message });
    }
    setInicializando(false);
  };

  return (
    <div>
      {!inicializado && (
        <div style={{ ...S.aviso, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <span>Las listas están vacías. Carga los valores predeterminados con un clic.</span>
          <button style={S.btn} onClick={() => setConfirmInit(true)} disabled={inicializando}>
            {inicializando ? 'Cargando...' : 'Cargar valores predeterminados'}
          </button>
        </div>
      )}
      {msgInit && <div style={msgInit.tipo === 'exito' ? S.exito : S.error}>{msgInit.texto}</div>}
      <div style={S.aviso}>
        Los cambios en estas listas se aplican inmediatamente en todos los módulos.
        No elimines un valor que esté en uso — los registros existentes lo seguirán mostrando correctamente, pero dejará de aparecer en los desplegables de nuevos registros.
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {LISTAS.map(l => (
          <button
            key={l.id}
            onClick={() => setListaActiva(l.id)}
            style={{
              ...S.btnSec,
              background:  listaActiva === l.id ? C.surface2 : 'none',
              color:       listaActiva === l.id ? '#fff'     : C.neblina,
              borderColor: listaActiva === l.id ? C.indigo   : C.borde,
            }}
          >
            {l.label}
          </button>
        ))}
      </div>

      <EditorLista coleccion={listaActiva} />
      {confirmInit && (
        <ModalConfirm
          mensaje="¿Cargar los valores predeterminados en las tres listas? Solo hazlo si están vacías."
          onAceptar={() => { setConfirmInit(false); inicializarListas(); }}
          onCancelar={() => setConfirmInit(false)}
          textoAceptar="Cargar"
          peligro={false}
        />
      )}
    </div>
  );
}

function EditorLista({ coleccion }) {
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nuevo, setNuevo]       = useState('');
  const [editando, setEditando]         = useState(null); // { id, valor }
  const [msg, setMsg]                   = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null); // id a eliminar

  const cargar = useCallback(async () => {
    setCargando(true);
    const snap = await getDocs(collection(db, 'config', 'listas', coleccion));
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => a.valor.localeCompare(b.valor, 'es', { sensitivity: 'base' }));
    setItems(items);
    setCargando(false);
  }, [coleccion]);

  useEffect(() => { cargar(); }, [cargar]);

  const añadir = async () => {
    const val = nuevo.trim();
    if (!val) return;
    await addDoc(collection(db, 'config', 'listas', coleccion), {
      valor: val,
      orden: items.length,
    });
    setNuevo('');
    cargar();
  };

  const guardarEdicion = async () => {
    if (!editando?.valor?.trim()) return;
    await updateDoc(doc(db, 'config', 'listas', coleccion, editando.id), { valor: editando.valor.trim() });
    setEditando(null);
    cargar();
  };

  const eliminar = async (id) => {
    setConfirmEliminar(id);
  };

  if (cargando) return <p style={{ color: C.neblina }}>Cargando...</p>;

  return (
    <div style={S.card}>
      {msg && <div style={msg.tipo === 'exito' ? S.exito : S.error}>{msg.texto}</div>}

      {/* Añadir nuevo */}
      <div style={{ ...S.fila, marginBottom: '20px' }}>
        <input
          style={{ ...S.input, flex: 1 }}
          value={nuevo}
          onChange={e => setNuevo(e.target.value)}
          placeholder="Nuevo valor..."
          onKeyDown={e => e.key === 'Enter' && añadir()}
        />
        <button style={S.btn} onClick={añadir}>Añadir</button>
      </div>

      <div style={S.separador} />

      {items.length === 0 ? (
        <p style={{ color: C.neblina, textAlign: 'center', padding: '24px' }}>Lista vacía. Añade el primer valor.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 4px', borderBottom: `1px solid ${C.borde}` }}>
              {editando?.id === item.id ? (
                <>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    value={editando.valor}
                    onChange={e => setEditando(ed => ({ ...ed, valor: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && guardarEdicion()}
                    autoFocus
                  />
                  <button style={S.btnSmall} onClick={guardarEdicion}>Guardar</button>
                  <button style={S.btnSmallSec} onClick={() => setEditando(null)}>Cancelar</button>
                </>
              ) : (
                <>
                  <span style={{ color: '#fff', flex: 1 }}>{item.valor}</span>
                  <button style={S.btnSmallSec} onClick={() => setEditando({ id: item.id, valor: item.valor })}>Editar</button>
                  <button style={{ ...S.btnSmallSec, borderColor: C.negativo, color: C.negativo }} onClick={() => eliminar(item.id)}>Eliminar</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {confirmEliminar && (
        <ModalConfirm
          mensaje="¿Eliminar este valor? Los registros existentes que lo usen seguirán mostrándolo correctamente, pero desaparecerá de los desplegables."
          onAceptar={async () => {
            await deleteDoc(doc(db, 'config', 'listas', coleccion, confirmEliminar));
            setConfirmEliminar(null);
            cargar();
          }}
          onCancelar={() => setConfirmEliminar(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PESTAÑA OBJETIVOS
// ════════════════════════════════════════════════════════════════════════════
const OBJETIVOS_DEF = [
  { id: 'proyectos',  label: 'Proyectos iniciados',         unidad: '',      inversion: false },
  { id: 'margen',     label: 'Margen empresa',               unidad: '%',     inversion: false },
  { id: 'velocidad',  label: 'Velocidad de entrega',         unidad: ' hr/m²',inversion: true  },
  { id: 'resenias',   label: 'Reseñas conseguidas',          unidad: '%',     inversion: false },
  { id: 'horas',      label: 'Horas registradas (equipo)',   unidad: ' h',    inversion: false },
];

const OBJETIVOS_DEFAULT = {
  proyectos: { clave: 9,    estelar: 11   },
  margen:    { clave: 20,   estelar: 28   },
  velocidad: { clave: 1.60, estelar: 1.40 },
  resenias:  { clave: 75,   estelar: 90   },
  horas:     { clave: 1672, estelar: 1712 },
};

function PestanaObjetivos() {
  const ANIO_ACTUAL  = new Date().getFullYear();
  const ANIO_SIGUIENTE = ANIO_ACTUAL + 1;

  const [anio, setAnio]         = useState(ANIO_ACTUAL);
  const [todosObjetivos, setTodosObjetivos] = useState({}); // { 2026: {...}, 2027: {...} }
  const [editando, setEditando] = useState(false);
  const [form, setForm]         = useState({});
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg]           = useState(null);

  const cargar = useCallback(async () => {
    const snap = await getDoc(doc(db, 'config', 'objetivos'));
    const data = snap.exists() ? snap.data() : {};
    setTodosObjetivos(data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Valores a mostrar: los del año seleccionado, o copia del actual si el siguiente no tiene datos
  const valores = todosObjetivos[anio]
    || (anio === ANIO_SIGUIENTE ? todosObjetivos[ANIO_ACTUAL] : null)
    || OBJETIVOS_DEFAULT;

  const esCopiaDelAnterior = anio === ANIO_SIGUIENTE && !todosObjetivos[ANIO_SIGUIENTE] && !!todosObjetivos[ANIO_ACTUAL];

  const iniciarEdicion = () => {
    setForm(JSON.parse(JSON.stringify(valores)));
    setEditando(true);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const snap = await getDoc(doc(db, 'config', 'objetivos'));
      const data = snap.exists() ? snap.data() : {};
      data[anio] = form;
      await setDoc(doc(db, 'config', 'objetivos'), data);
      setTodosObjetivos(data);
      setEditando(false);
      setMsg({ tipo: 'exito', texto: `Objetivos ${anio} guardados.` });
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message });
    }
    setGuardando(false);
  };

  return (
    <div>
      {msg && <div style={msg.tipo === 'exito' ? S.exito : S.error}>{msg.texto}</div>}

      {/* Pills de año */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[ANIO_ACTUAL, ANIO_SIGUIENTE].map(a => (
          <button
            key={a}
            onClick={() => { setAnio(a); setEditando(false); }}
            style={{
              ...S.btnSec,
              background:  anio === a ? C.surface2 : 'none',
              color:       anio === a ? '#fff'     : C.neblina,
              borderColor: anio === a ? C.indigo   : C.borde,
              fontWeight:  anio === a ? 500        : 400,
            }}
          >
            {a}
          </button>
        ))}
      </div>

      {esCopiaDelAnterior && (
        <div style={S.aviso}>
          Los objetivos de {ANIO_SIGUIENTE} aún no están configurados. Se muestran los de {ANIO_ACTUAL} como punto de partida. Edítalos y guárdalos para fijar los objetivos de {ANIO_SIGUIENTE}.
        </div>
      )}

      <div style={{ ...S.aviso, marginBottom: '20px' }}>
        <strong>Objetivo Clave</strong> — lo que el equipo se propone lograr este año.<br />
        <strong>Objetivo Estelar</strong> — superarse. En velocidad, menor valor es mejor.
      </div>

      <div style={S.card}>
        <table style={S.tabla}>
          <thead>
            <tr>
              <th style={S.th}>Objetivo</th>
              <th style={S.th}>Clave</th>
              <th style={S.th}>Estelar</th>
            </tr>
          </thead>
          <tbody>
            {OBJETIVOS_DEF.map(obj => (
              <tr key={obj.id}>
                <td style={S.td}>
                  <span style={{ color: '#fff' }}>{obj.label}</span>
                  {obj.inversion && <span style={{ color: C.neblina, fontSize: '0.75rem', marginLeft: '6px' }}>(menor = mejor)</span>}
                </td>
                <td style={S.td}>
                  {editando ? (
                    <input
                      style={{ ...S.input, width: '100px' }}
                      type="number"
                      step="0.01"
                      value={form[obj.id]?.clave ?? ''}
                      onChange={e => setForm(f => ({ ...f, [obj.id]: { ...f[obj.id], clave: parseFloat(e.target.value) } }))}
                    />
                  ) : (
                    <span style={{ color: C.violeta, fontWeight: 500 }}>
                      {valores[obj.id]?.clave}{obj.unidad}
                    </span>
                  )}
                </td>
                <td style={S.td}>
                  {editando ? (
                    <input
                      style={{ ...S.input, width: '100px' }}
                      type="number"
                      step="0.01"
                      value={form[obj.id]?.estelar ?? ''}
                      onChange={e => setForm(f => ({ ...f, [obj.id]: { ...f[obj.id], estelar: parseFloat(e.target.value) } }))}
                    />
                  ) : (
                    <span style={{ color: C.atencion, fontWeight: 500 }}>
                      {valores[obj.id]?.estelar}{obj.unidad}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
          {editando ? (
            <>
              <button style={S.btnSec} onClick={() => setEditando(false)}>Cancelar</button>
              <button style={S.btn} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : `Guardar objetivos ${anio}`}
              </button>
            </>
          ) : (
            <button style={S.btn} onClick={iniciarEdicion}>Editar objetivos {anio}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PESTAÑA MARGEN EMPRESA
// ════════════════════════════════════════════════════════════════════════════
function PestanaMargen() {
  const anioActual = new Date().getFullYear();
  const mesActual  = new Date().getMonth(); // 0-based
  const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const [anio, setAnio]     = useState(anioActual);
  const [datos, setDatos]   = useState({});
  const [editando, setEditando] = useState(null); // índice de mes
  const [valorEdit, setValorEdit] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg]       = useState(null);

  const cargar = useCallback(async () => {
    const ref = doc(db, 'config', 'margen_empresa');
    const snap = await getDoc(ref);
    if (snap.exists()) setDatos(snap.data());
    else setDatos({});
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const clave = `${anio}`;
  const datosAnio = datos[clave] || {};

  const guardarMes = async (mes) => {
    setGuardando(true);
    try {
      const valor = parseFloat(valorEdit);
      if (isNaN(valor)) { setMsg({ tipo: 'error', texto: 'Introduce un número válido.' }); setGuardando(false); return; }
      const nuevos = { ...datos, [clave]: { ...datosAnio, [mes]: valor } };
      await setDoc(doc(db, 'config', 'margen_empresa'), nuevos);
      setDatos(nuevos);
      setEditando(null);
      setMsg({ tipo: 'exito', texto: `Margen de ${MESES[mes]} ${anio} guardado.` });
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.message });
    }
    setGuardando(false);
  };

  const margenMedio = () => {
    const vals = Object.values(datosAnio).filter(v => typeof v === 'number');
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  return (
    <div>
      {msg && <div style={msg.tipo === 'exito' ? S.exito : S.error}>{msg.texto}</div>}

      <div style={S.aviso}>
        El margen empresa es el margen global del estudio cada mes, introducido manualmente.
        No es el margen de los proyectos — es el dato que aparece en el Dashboard de objetivos.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <label style={{ ...S.label, margin: 0 }}>Año</label>
        <select style={{ ...S.select, width: 'auto' }} value={anio} onChange={e => setAnio(Number(e.target.value))}>
          {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {margenMedio() !== null && (
          <span style={{ color: C.violeta, fontSize: '0.9rem' }}>
            Media {anio}: <strong>{margenMedio()}%</strong>
          </span>
        )}
      </div>

      <div style={S.card}>
        <table style={S.tabla}>
          <thead>
            <tr>
              <th style={S.th}>Mes</th>
              <th style={S.th}>Margen empresa (%)</th>
              <th style={S.th} />
            </tr>
          </thead>
          <tbody>
            {MESES.map((mes, i) => {
              const valor = datosAnio[i];
              const esFuturo = anio === anioActual && i > mesActual;
              return (
                <tr key={i}>
                  <td style={{ ...S.td, color: i === mesActual && anio === anioActual ? '#fff' : C.neblina, fontWeight: i === mesActual && anio === anioActual ? 500 : 400 }}>
                    {mes}
                    {i === mesActual && anio === anioActual && <span style={{ color: C.indigo, fontSize: '0.72rem', marginLeft: '6px' }}>← mes actual</span>}
                  </td>
                  <td style={S.td}>
                    {editando === i ? (
                      <input
                        style={{ ...S.input, width: '100px' }}
                        type="number"
                        step="0.1"
                        value={valorEdit}
                        onChange={e => setValorEdit(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && guardarMes(i)}
                        autoFocus
                      />
                    ) : (
                      <span style={{ color: valor !== undefined ? C.violeta : C.neblina, fontWeight: valor !== undefined ? 500 : 400 }}>
                        {valor !== undefined ? `${valor}%` : esFuturo ? '—' : 'Sin datos'}
                      </span>
                    )}
                  </td>
                  <td style={S.td}>
                    {editando === i ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={S.btnSmall} onClick={() => guardarMes(i)} disabled={guardando}>Guardar</button>
                        <button style={S.btnSmallSec} onClick={() => setEditando(null)}>Cancelar</button>
                      </div>
                    ) : !esFuturo ? (
                      <button style={S.btnSmallSec} onClick={() => { setEditando(i); setValorEdit(valor !== undefined ? String(valor) : ''); }}>
                        {valor !== undefined ? 'Editar' : 'Añadir'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PESTAÑA EXPORTACIÓN
// ════════════════════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────
// PESTAÑA ESTRUCTURA DE COSTES
// ─────────────────────────────────────────────
function PestanaEstructura() {
  const ANIO_ACTUAL = new Date().getFullYear();
  const ANIO_ANTERIOR = ANIO_ACTUAL - 1;
  const ANIOS = [ANIO_ACTUAL, ANIO_ANTERIOR, ANIO_ANTERIOR - 1];

  const [datos, setDatos] = useState({}); // { 2026: 12000, 2025: 10000, ... }
  const [editando, setEditando] = useState(null); // año en edición
  const [valorEdit, setValorEdit] = useState('');
  const [msg, setMsg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const cargar = async () => {
      try {
        const snap = await getDocs(collection(db, 'config', 'estructura_costes', 'anios'));
        const d = {};
        snap.docs.forEach(doc => { d[parseInt(doc.id)] = doc.data().gastos || 0; });
        setDatos(d);
      } catch (e) { console.error('Error cargando estructura costes:', e); }
    };
    cargar();
  }, []);

  const iniciarEdicion = (anio) => {
    setEditando(anio);
    setValorEdit(datos[anio] !== undefined ? String(datos[anio]) : '');
  };

  const guardar = async (anio) => {
    const valor = parseFloat(valorEdit.replace(',', '.'));
    if (isNaN(valor) || valor < 0) {
      setMsg({ tipo: 'error', texto: 'Introduce un importe válido.' });
      return;
    }
    setGuardando(true);
    try {
      await setDoc(doc(db, 'config', 'estructura_costes', 'anios', String(anio)), { gastos: valor });
      setDatos(prev => ({ ...prev, [anio]: valor }));
      setEditando(null);
      setMsg({ tipo: 'exito', texto: `Gastos de estructura ${anio} guardados.` });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ tipo: 'error', texto: 'Error al guardar. Inténtalo de nuevo.' });
    } finally { setGuardando(false); }
  };

  const fmtEur = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';

  return (
    <div style={{ maxWidth: 600 }}>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: 6 }}>Estructura de costes</h2>
      <p style={{ fontSize: 13, color: C.neblina, marginBottom: 24, lineHeight: 1.6 }}>
        Introduce los gastos anuales de estructura del estudio: alquiler, software, seguros, suministros, equipos y cualquier gasto que no sean nóminas de personal. Se usa para calcular el <strong>coste/hora real de la empresa</strong> en el módulo de Análisis.
      </p>

      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.tipo === 'exito' ? 'rgba(29,158,117,0.15)' : 'rgba(216,90,48,0.15)',
          border: `1px solid ${msg.tipo === 'exito' ? '#1D9E75' : '#D85A30'}`,
          color: msg.tipo === 'exito' ? '#1D9E75' : '#D85A30',
        }}>{msg.texto}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ANIOS.map(anio => {
          const valor = datos[anio];
          const esCurrent = anio === ANIO_ACTUAL;
          const enEdicion = editando === anio;
          return (
            <div key={anio} style={{
              background: C.surface2, border: `1px solid ${esCurrent ? C.indigo : 'rgba(83,74,183,0.25)'}`,
              borderRadius: 12, padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
              <div style={{ minWidth: 60 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: esCurrent ? '#fff' : C.neblina }}>{anio}</span>
                {esCurrent && <span style={{ marginLeft: 8, fontSize: 11, background: C.indigo, color: '#fff', borderRadius: 4, padding: '1px 6px' }}>Actual</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: C.neblina, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Gastos de estructura</div>
                {enEdicion ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number" value={valorEdit} onChange={e => setValorEdit(e.target.value)}
                      placeholder="0.00" min="0" step="100"
                      style={{ width: 160, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.indigo}`, background: '#1a1440', color: '#fff', fontSize: 14, outline: 'none' }}
                      autoFocus onKeyDown={e => { if (e.key === 'Enter') guardar(anio); if (e.key === 'Escape') setEditando(null); }}
                    />
                    <span style={{ color: C.neblina, fontSize: 13 }}>€ / año</span>
                    <button onClick={() => guardar(anio)} disabled={guardando} style={{ padding: '6px 14px', borderRadius: 6, background: C.indigo, border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                      {guardando ? '...' : 'Guardar'}
                    </button>
                    <button onClick={() => setEditando(null)} style={{ padding: '6px 10px', borderRadius: 6, background: 'transparent', border: `1px solid rgba(83,74,183,0.3)`, color: C.neblina, fontSize: 13, cursor: 'pointer' }}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: valor !== undefined ? '#fff' : C.neblina }}>
                      {valor !== undefined ? fmtEur(valor) : '—'}
                    </span>
                    <button onClick={() => iniciarEdicion(anio)} style={{ padding: '4px 12px', borderRadius: 6, background: 'transparent', border: `1px solid rgba(83,74,183,0.4)`, color: C.neblina, fontSize: 12, cursor: 'pointer' }}>
                      {valor !== undefined ? 'Editar' : '+ Introducir'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20, padding: '12px 16px', borderRadius: 8, background: 'rgba(186,117,23,0.1)', border: '1px solid rgba(186,117,23,0.25)', fontSize: 12, color: '#e8a93a' }}>
        ⚠ Introduce los gastos del año vencido una vez cerrado el ejercicio. Mientras no estén disponibles los datos del año actual, Análisis usará los del año anterior como estimación y lo indicará con un aviso.
      </div>
    </div>
  );
}

function PestanaExportacion() {
  const [anio, setAnio] = useState('todos');
  const [exportando, setExportando] = useState(null); // id del módulo en curso o 'todo'
  const [progreso, setProgreso] = useState('');
  const [msg, setMsg] = useState(null);

  // ── Helper: convierte fecha Firestore o string a dd/mm/aaaa ──────────────────
  const fmtFecha = (v) => {
    if (!v) return '';
    const d = v?.toDate ? v.toDate() : (typeof v === 'string' ? new Date(v) : null);
    if (!d || isNaN(d)) return String(v);
    return d.toLocaleDateString('es-ES');
  };

  // ── Helper: escapa un valor para CSV (punto y coma como separador) ────────────
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/\r?\n/g, ' ');
    return s.includes(';') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // ── Helper: descarga un CSV ──────────────────────────────────────────────────
  const descargarCSV = (nombre, cabeceras, filas) => {
    const contenido = '\uFEFF' + [cabeceras.join(';'), ...filas.map(f => f.map(esc).join(';'))].join('\n');
    const blob = new Blob([contenido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `metron_${nombre}${anio !== 'todos' ? `_${anio}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    return filas.length;
  };

  // ── Helper: filtra por año ───────────────────────────────────────────────────
  const filtrarAnio = (rows, campoFecha) => {
    if (anio === 'todos') return rows;
    const a = parseInt(anio);
    return rows.filter(r => {
      const v = r[campoFecha];
      if (!v) return false;
      const d = v?.toDate ? v.toDate() : new Date(v);
      return !isNaN(d) && d.getFullYear() === a;
    });
  };

  // ── Carga datos de referencia (una vez por exportación completa) ─────────────
  const cargarReferencias = async () => {
    const [snLeads, snClientes, snUsuarios, snProyectos] = await Promise.all([
      getDocs(collection(db, 'leads')),
      getDocs(collection(db, 'clientes')),
      getDocs(collection(db, 'usuarios')),
      getDocs(collection(db, 'proyectos')),
    ]);
    const leads = {}; snLeads.docs.forEach(d => { leads[d.id] = d.data(); });
    const clientes = {}; snClientes.docs.forEach(d => { clientes[d.id] = d.data(); });
    const usuarios = {}; snUsuarios.docs.forEach(d => { usuarios[d.id] = d.data(); });
    const proyectos = {}; snProyectos.docs.forEach(d => { proyectos[d.id] = d.data(); });
    return { leads, clientes, usuarios, proyectos };
  };

  // ── Nombre legible de un cliente ─────────────────────────────────────────────
  const nombreCliente = (c) => c ? (c.razon_social || `${c.nombre || ''} ${c.apellidos || ''}`.trim()) : '';
  const nombreLead = (l) => l ? `${l.nombre || ''} ${l.apellidos || ''}`.trim() : '';
  const nombreUsuario = (u) => u ? (u.nombre || u.email || '') : '';

  // ════════════════════════════════════════════════════════════════════════════
  // EXPORTACIONES POR MÓDULO
  // ════════════════════════════════════════════════════════════════════════════

  const exportarLeads = async (refs) => {
    const snap = await getDocs(collection(db, 'leads'));
    const rows = filtrarAnio(snap.docs.map(d => ({ _id: d.id, ...d.data() })), 'fecha_alta');
    const cab = ['id_lead','nombre','apellidos','empresa','email','movil','telefono','fuente',
                 'direccion','localidad','provincia','pais','notas','fecha_alta','creado_en'];
    const filas = rows.map(r => [
      r._id, r.nombre, r.apellidos, r.empresa, r.email, r.movil, r.telefono, r.fuente,
      r.direccion, r.localidad, r.provincia, r.pais, r.notas,
      fmtFecha(r.fecha_alta), fmtFecha(r.creado_en),
    ]);
    return descargarCSV('leads', cab, filas);
  };

  const exportarOportunidades = async (refs) => {
    // Leer subcollecciones de cada lead una a una (sin collectionGroup)
    const snLeads = await getDocs(collection(db, 'leads'));
    const todasOps = [];
    for (const ldoc of snLeads.docs) {
      const snOps = await getDocs(collection(db, 'leads', ldoc.id, 'posibles_proyectos'));
      snOps.docs.forEach(d => todasOps.push({ _id: d.id, _leadId: ldoc.id, _leadNombre: nombreLead(ldoc.data()), ...d.data() }));
    }
    const rows = anio === 'todos' ? todasOps : todasOps.filter(r => {
      const d = r.fecha_entrada?.toDate ? r.fecha_entrada.toDate() : new Date(r.fecha_entrada || '');
      return !isNaN(d) && d.getFullYear() === parseInt(anio);
    });
    const cab = ['id_oportunidad','id_lead','lead','estado','sector','fecha_entrada',
                 'superficie','presupuesto_estimado','direccion','cp','ciudad','provincia','pais','observaciones','creado_en'];
    const filas = rows.map(r => [
      r._id, r._leadId, r._leadNombre, r.estado, r.sector, fmtFecha(r.fecha_entrada),
      r.superficie, r.presupuesto_estimado,
      r.direccion, r.cp, r.ciudad, r.provincia, r.pais, r.observaciones, fmtFecha(r.creado_en),
    ]);
    return descargarCSV('oportunidades', cab, filas);
  };

  const exportarPresupuestos = async (refs) => {
    const snap = await getDocs(collection(db, 'presupuestos'));
    const rows = filtrarAnio(snap.docs.map(d => ({ _id: d.id, ...d.data() })), 'fecha_presupuesto');
    const cab = ['id_presupuesto','num_presupuesto','fecha_presupuesto','id_lead','lead','id_oportunidad',
                 'sector','tipo_encargo','importe','superficie','importe_m2','descripcion','estado',
                 'num_encargo','fecha_encargo','importe_encargo','oportunidad_ciudad','creado_en'];
    const filas = rows.map(r => {
      const lead = refs.leads[r.leadId] || {};
      return [
        r._id, r.num_presupuesto, fmtFecha(r.fecha_presupuesto),
        r.leadId, nombreLead(lead), r.oportunidadId,
        r.sector, r.tipo_encargo, r.importe, r.superficie, r.importe_m2, r.descripcion, r.estado,
        r.num_encargo, fmtFecha(r.fecha_encargo), r.importe_encargo, r.oportunidad_ciudad,
        fmtFecha(r.creado_en),
      ];
    });
    return descargarCSV('presupuestos', cab, filas);
  };

  const exportarClientes = async () => {
    const snap = await getDocs(collection(db, 'clientes'));
    const rows = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
    const cab = ['id_cliente','tipo','nombre','apellidos','razon_social','nif',
                 'direccion','cp','localidad','provincia','pais',
                 'c1_nombre','c1_cargo','c1_movil','c1_telefono','c1_email',
                 'c2_nombre','c2_cargo','c2_movil','c2_telefono','c2_email',
                 'notas','creado_en'];
    const filas = rows.map(r => [
      r._id, r.tipo, r.nombre, r.apellidos, r.razon_social, r.nif,
      r.direccion, r.cp, r.localidad, r.provincia, r.pais,
      r.c1_nombre, r.c1_cargo, r.c1_movil, r.c1_telefono, r.c1_email,
      r.c2_nombre, r.c2_cargo, r.c2_movil, r.c2_telefono, r.c2_email,
      r.notas, fmtFecha(r.creado_en),
    ]);
    return descargarCSV('clientes', cab, filas);
  };

  const exportarProyectos = async (refs) => {
    const snap = await getDocs(collection(db, 'proyectos'));
    const rows = filtrarAnio(snap.docs.map(d => ({ _id: d.id, ...d.data() })), 'fecha_inicio');
    const cab = ['id_proyecto','codigo','nombre','id_cliente','cliente','id_presupuesto',
                 'sector','tipo_proyecto','fuente','num_encargo','estado',
                 'superficie','direccion','cp','ciudad','provincia','pais',
                 'importe_encargo','importe_facturado','costes_produccion',
                 'fecha_inicio','fecha_entrega_proyecto','fecha_fin_obra',
                 'resenia','observaciones','creado_en'];
    const filas = rows.map(r => {
      const cliente = refs.clientes[r.clienteId] || {};
      return [
        r._id, r.codigo, r.nombre, r.clienteId, nombreCliente(cliente), r.presupuestoId,
        r.sector, r.tipo_proyecto, r.fuente, r.num_encargo, r.estado,
        r.superficie, r.direccion, r.cp, r.ciudad, r.provincia, r.pais,
        r.importe_encargo, r.importe_facturado, r.costes_produccion,
        fmtFecha(r.fecha_inicio), fmtFecha(r.fecha_entrega_proyecto), fmtFecha(r.fecha_fin_obra),
        r.resenia ? 'Sí' : 'No', r.observaciones, fmtFecha(r.creado_en),
      ];
    });
    return descargarCSV('proyectos', cab, filas);
  };

  const exportarHoras = async (refs) => {
    // Sesión 33: con un año elegido se piden al servidor solo los registros de ese año (fecha es texto AAAA-MM-DD).
    const base = collection(db, 'registros_horas');
    const consulta = anio === 'todos'
      ? base
      : query(base, where('fecha', '>=', `${anio}-01-01`), where('fecha', '<=', `${anio}-12-31`));
    const snap = await getDocs(consulta);
    const rows = filtrarAnio(snap.docs.map(d => ({ _id: d.id, ...d.data() })), 'fecha');
    const cab = ['id_registro','id_empleado','empleado','id_proyecto','codigo_proyecto','nombre_proyecto',
                 'fecha','horas','es_interno','categoria','notas','creado_en'];
    const filas = rows.map(r => {
      const usuario = refs.usuarios[r.empleadoId] || {};
      const proyecto = refs.proyectos[r.proyectoId] || {};
      return [
        r._id, r.empleadoId, nombreUsuario(usuario),
        r.proyectoId || '', proyecto.codigo || '', proyecto.nombre || '',
        r.fecha, r.horas, r.esInterno ? 'Sí' : 'No', r.categoria || '', r.nota || '',
        fmtFecha(r.creado_en),
      ];
    });
    return descargarCSV('horas', cab, filas);
  };

  const exportarUsuarios = async () => {
    const snap = await getDocs(collection(db, 'usuarios'));
    const rows = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
    const cab = ['id_usuario','nombre','email','roles','estado','activo','creado_en'];
    const filas = rows.map(r => [
      r._id, r.nombre, r.email,
      Array.isArray(r.roles) ? r.roles.join(',') : (r.rol || ''),
      r.estado || '', r.activo !== undefined ? (r.activo ? 'Sí' : 'No') : '',
      fmtFecha(r.creado_en),
    ]);
    return descargarCSV('usuarios', cab, filas);
  };

  const exportarTarifas = async () => {
    const snUsuarios = await getDocs(collection(db, 'usuarios'));
    const todasTarifas = [];
    for (const udoc of snUsuarios.docs) {
      const snTarifas = await getDocs(collection(db, 'usuarios', udoc.id, 'tarifas'));
      const u = udoc.data();
      snTarifas.docs.forEach(d => todasTarifas.push({
        _id: d.id, _usuarioId: udoc.id, _usuarioNombre: nombreUsuario(u), ...d.data()
      }));
    }
    const cab = ['id_tarifa','id_usuario','empleado','coste_empresa','horas_diarias',
                 'dias_vacaciones','dias_festivos','horas_anuales','coste_hora',
                 'fecha_inicio','fecha_fin','notas'];
    const filas = todasTarifas.map(r => [
      r._id, r._usuarioId, r._usuarioNombre,
      r.coste_empresa, r.horas_diarias, r.dias_vacaciones, r.dias_festivos,
      r.horas_anuales, r.coste_hora,
      r.fecha_inicio || '', r.fecha_fin || '', r.notas || '',
    ]);
    return descargarCSV('tarifas', cab, filas);
  };

  // ── Mapa de módulos para el UI ───────────────────────────────────────────────
  const MODULOS = [
    { id: 'leads',          label: 'Leads',          desc: 'Contactos comerciales con todos sus datos' },
    { id: 'oportunidades',  label: 'Oportunidades',  desc: 'Posibles proyectos vinculados a cada lead' },
    { id: 'presupuestos',   label: 'Presupuestos',   desc: 'Todos los presupuestos con datos del encargo' },
    { id: 'clientes',       label: 'Clientes',       desc: 'Empresas y particulares con ambos contactos' },
    { id: 'proyectos',      label: 'Proyectos',      desc: 'Proyectos completos con fechas y económicos' },
    { id: 'horas',          label: 'Control de horas', desc: 'Registros de horas con empleado y proyecto' },
    { id: 'usuarios',       label: 'Usuarios',       desc: 'Equipo activo e histórico con roles' },
    { id: 'tarifas',        label: 'Tarifas',        desc: 'Historial de coste/hora por empleado' },
  ];

  const ejecutarExportacion = async (id, refs) => {
    switch (id) {
      case 'leads':         return exportarLeads(refs);
      case 'oportunidades': return exportarOportunidades(refs);
      case 'presupuestos':  return exportarPresupuestos(refs);
      case 'clientes':      return exportarClientes();
      case 'proyectos':     return exportarProyectos(refs);
      case 'horas':         return exportarHoras(refs);
      case 'usuarios':      return exportarUsuarios();
      case 'tarifas':       return exportarTarifas();
      default: return 0;
    }
  };

  const exportarModulo = async (id) => {
    setExportando(id);
    setMsg(null);
    try {
      const refs = await cargarReferencias();
      const n = await ejecutarExportacion(id, refs);
      setMsg({ tipo: 'exito', texto: `${n} registros exportados.` });
    } catch (err) {
      setMsg({ tipo: 'error', texto: `Error: ${err.message}` });
    }
    setExportando(null);
  };

  const exportarTodo = async () => {
    setExportando('todo');
    setMsg(null);
    try {
      const refs = await cargarReferencias();
      let total = 0;
      for (const mod of MODULOS) {
        setProgreso(`Exportando ${mod.label}...`);
        const n = await ejecutarExportacion(mod.id, refs);
        total += n;
        await new Promise(r => setTimeout(r, 500));
      }
      setProgreso('');
      setMsg({ tipo: 'exito', texto: `Exportacion completa — ${MODULOS.length} archivos descargados.` });
    } catch (err) {
      setProgreso('');
      setMsg({ tipo: 'error', texto: `Error: ${err.message}` });
    }
    setExportando(null);
  };

  return (
    <div>
      {msg && <div style={msg.tipo === 'exito' ? S.exito : S.error}>{msg.texto}</div>}

      <div style={S.aviso}>
        Exportacion completa de todos los datos de METRON. Los CSV usan punto y coma como separador y codificacion UTF-8 con BOM — compatibles con Excel en español. Los IDs internos se incluyen para permitir reimportacion o migracion a otra app.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <label style={{ ...S.label, margin: 0 }}>Filtrar por año</label>
        <select style={{ ...S.select, width: 'auto' }} value={anio} onChange={e => setAnio(e.target.value)}>
          <option value="todos">Todos los años</option>
          {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1]
            .map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <span style={{ fontSize: '0.8rem', color: C.neblina }}>
          Usuarios y Tarifas siempre exportan el historial completo (sin filtro de año)
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
        {MODULOS.map(mod => (
          <div key={mod.id} style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, padding: '14px 20px' }}>
            <div>
              <p style={{ color: '#fff', fontWeight: 600, marginBottom: '2px', fontSize: '0.9rem' }}>{mod.label}</p>
              <p style={{ color: C.neblina, fontSize: '0.78rem', margin: 0 }}>{mod.desc}</p>
              {mod.id === 'horas' && anio === 'todos' && (
                <p style={{ color: C.neblina, fontSize: '0.75rem', margin: '2px 0 0', opacity: 0.8 }}>
                  Todos los años descarga el historial completo de horas (más de 9.000 lecturas). Elige un año para leer solo ese año.
                </p>
              )}
            </div>
            <button
              style={{ ...S.btnSec, minWidth: 120 }}
              onClick={() => exportarModulo(mod.id)}
              disabled={!!exportando}
            >
              {exportando === mod.id ? 'Exportando...' : 'Exportar CSV'}
            </button>
          </div>
        ))}
      </div>

      {progreso && (
        <div style={{ textAlign: 'center', color: C.neblina, fontSize: '0.85rem', marginBottom: 12 }}>
          {progreso}
        </div>
      )}

      <div style={S.separador} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
        <span style={{ color: C.neblina, fontSize: '0.82rem' }}>
          {MODULOS.length} archivos — copia de seguridad completa
        </span>
        <button style={S.btn} onClick={exportarTodo} disabled={!!exportando}>
          {exportando === 'todo' ? progreso || 'Exportando...' : `Exportar todo (${MODULOS.length} archivos)`}
        </button>
      </div>
    </div>
  );
}

// ─── Modal de confirmación reutilizable ───────────────────────────────────────
function ModalConfirm({ mensaje, onAceptar, onCancelar, textoCancelar, textoAceptar = 'Confirmar', peligro = true }) {
  // Sesión 34: diálogo común; la opción segura es la destacada y Enter/Esc la eligen
  return (
    <DialogoConfirmar
      titulo={peligro ? 'Confirmar' : 'Confirmar acción'}
      mensaje={mensaje}
      textoSeguro={textoCancelar || (peligro ? 'No, volver' : 'Cancelar')}
      textoPeligroso={textoAceptar}
      onSeguro={onCancelar}
      onPeligroso={onAceptar}
    />
  );
}

// ─── Estilos modal ────────────────────────────────────────────────────────────
const estilosModal = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(14,11,36,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  caja: {
    background: C.noche, border: `1px solid ${C.borde}`,
    borderRadius: '16px', padding: '32px',
    width: '100%', maxWidth: '520px',
    maxHeight: '90vh', overflowY: 'auto',
  },
};

export default Configuracion;
